import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Privacy Policy
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Introduction
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Welcome to Ithopu ("we," "our," or "us"). We are committed to
              protecting your privacy and ensuring the security of your personal
              information. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our mental
              math learning platform, including our website and mobile
              application (collectively, the "Service").
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              By using our Service, you agree to the collection and use of
              information in accordance with this policy. If you do not agree
              with our policies and practices, please do not use our Service.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Information We Collect
            </h2>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Personal Information
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              When you create an account, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>Email address</li>
              <li>Username</li>
              <li>Password (encrypted)</li>
              <li>Age or date of birth (for age-appropriate content)</li>
              <li>Parent/guardian information (for users under 13)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Usage Information
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We automatically collect information about how you use our
              Service:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>Learning progress and performance data</li>
              <li>Practice session history</li>
              <li>Tournament participation and scores</li>
              <li>Technique mastery levels</li>
              <li>Time spent on different activities</li>
              <li>Device information (type, operating system, browser)</li>
              <li>IP address and general location (country/region)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Analytics and Cookies
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We use cookies and similar tracking technologies to track activity
              on our Service and store certain information. You can instruct
              your browser to refuse all cookies or to indicate when a cookie is
              being sent.
            </p>
          </section>

          {/* How We Use Your Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              How We Use Your Information
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We use the collected information for various purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>
                <strong>Provide and maintain our Service</strong> - Enable core
                functionality and features
              </li>
              <li>
                <strong>Personalize your experience</strong> - Recommend
                techniques and adjust difficulty levels
              </li>
              <li>
                <strong>Track progress</strong> - Monitor learning achievements
                and provide analytics
              </li>
              <li>
                <strong>Improve our Service</strong> - Analyze usage patterns to
                enhance features
              </li>
              <li>
                <strong>Communicate with you</strong> - Send updates,
                notifications, and support messages
              </li>
              <li>
                <strong>Ensure security</strong> - Detect and prevent fraud,
                abuse, and security incidents
              </li>
              <li>
                <strong>Comply with legal obligations</strong> - Meet regulatory
                requirements
              </li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Children's Privacy (COPPA Compliance)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              Our Service is designed for students ages 8-16. We take children's
              privacy seriously and comply with the Children's Online Privacy
              Protection Act (COPPA).
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              For Users Under 13
            </h3>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4 mb-4">
              <li>
                We require verifiable parental consent before collecting
                personal information
              </li>
              <li>
                Parents can review, delete, or refuse further collection of
                their child's information
              </li>
              <li>
                We collect only information necessary to provide the Service
              </li>
              <li>
                We do not share children's information with third parties for
                marketing purposes
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Parental Rights
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Parents and guardians have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>Review their child's personal information</li>
              <li>Request deletion of their child's information</li>
              <li>
                Refuse further collection or use of their child's information
              </li>
              <li>
                Access their child's learning progress through the Parent Portal
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              To exercise these rights, please contact us at privacy@ithopu.com
            </p>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              How We Share Your Information
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              We do not sell your personal information. We may share your
              information in the following circumstances:
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Service Providers
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We work with third-party service providers who help us operate our
              Service:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>
                <strong>Supabase</strong> - Database and authentication services
              </li>
              <li>
                <strong>Firebase</strong> - Hosting and analytics
              </li>
              <li>
                <strong>Vercel</strong> - Web hosting and deployment
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
              These providers are contractually obligated to protect your
              information and use it only for the purposes we specify.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">
              Legal Requirements
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may disclose your information if required by law or in response
              to valid requests by public authorities (e.g., court orders,
              subpoenas).
            </p>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Data Security
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              We implement appropriate technical and organizational measures to
              protect your personal information:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication using industry-standard protocols</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and monitoring</li>
              <li>Secure data centers with physical security measures</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              However, no method of transmission over the Internet or electronic
              storage is 100% secure. While we strive to protect your
              information, we cannot guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Data Retention
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We retain your personal information for as long as necessary to
              provide our Service and fulfill the purposes outlined in this
              Privacy Policy. When you delete your account, we will delete or
              anonymize your personal information within 30 days, except where
              we are required to retain it for legal purposes.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              Learning progress data may be retained in anonymized form for
              research and service improvement purposes.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Your Privacy Rights
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              Depending on your location, you may have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>
                <strong>Access</strong> - Request a copy of your personal
                information
              </li>
              <li>
                <strong>Correction</strong> - Request correction of inaccurate
                information
              </li>
              <li>
                <strong>Deletion</strong> - Request deletion of your personal
                information
              </li>
              <li>
                <strong>Portability</strong> - Request transfer of your data to
                another service
              </li>
              <li>
                <strong>Objection</strong> - Object to processing of your
                information
              </li>
              <li>
                <strong>Restriction</strong> - Request restriction of processing
              </li>
              <li>
                <strong>Withdraw Consent</strong> - Withdraw consent for data
                processing
              </li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              To exercise these rights, please contact us at privacy@ithopu.com.
              We will respond to your request within 30 days.
            </p>
          </section>

          {/* International Users */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              International Users
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Our Service is operated in the United States. If you are accessing
              our Service from outside the United States, please be aware that
              your information may be transferred to, stored, and processed in
              the United States where our servers are located and our central
              database is operated.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              By using our Service, you consent to the transfer of your
              information to the United States and processing in accordance with
              this Privacy Policy.
            </p>
          </section>

          {/* Third-Party Links */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Third-Party Links
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Our Service may contain links to third-party websites or services
              that are not operated by us. We have no control over and assume no
              responsibility for the content, privacy policies, or practices of
              any third-party sites or services. We encourage you to review the
              privacy policy of every site you visit.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Changes to This Privacy Policy
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify
              you of any changes by posting the new Privacy Policy on this page
              and updating the "Last updated" date at the top of this policy.
            </p>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              For significant changes, we will provide a more prominent notice
              (including, for certain services, email notification of privacy
              policy changes). We encourage you to review this Privacy Policy
              periodically for any changes.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or our privacy
              practices, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 space-y-2">
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Email:</strong> privacy@ithopu.com
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Website:</strong>{" "}
                <a
                  href="https://ithopu.com"
                  className="text-blue-600 hover:text-blue-700"
                >
                  https://ithopu.com
                </a>
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Address:</strong> [Your Company Address]
              </p>
            </div>
          </section>

          {/* GDPR Notice */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              GDPR Notice (For EU Users)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              If you are located in the European Economic Area (EEA), you have
              additional rights under the General Data Protection Regulation
              (GDPR):
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>Right to be informed about data collection and use</li>
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Rights related to automated decision-making</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              Our legal basis for processing your data is your consent, which
              you provide when creating an account and using our Service.
            </p>
          </section>

          {/* California Privacy Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              California Privacy Rights (CCPA)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
              If you are a California resident, you have specific rights under
              the California Consumer Privacy Act (CCPA):
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 ml-4">
              <li>Right to know what personal information is collected</li>
              <li>
                Right to know if personal information is sold or disclosed
              </li>
              <li>Right to say no to the sale of personal information</li>
              <li>Right to access your personal information</li>
              <li>Right to delete personal information</li>
              <li>Right to non-discrimination for exercising your rights</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-4">
              <strong>We do not sell your personal information.</strong>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
